/**
 * Co-parentage — Livre de liaison + Journal (port `vCpLiaison` l. 2867
 * + `vCpJournal` l. 2044 du proto).
 *
 * Phase 1, incrément 15. Une vue unique avec 3 tabs :
 *   - **Transmissions** : transmissions formelles avec statuts
 *     `brouillon` / `envoyé` / `signé A` / `signé B` /
 *     `signé par les deux`. Boutons sign A/B sur les entrées
 *     `signatureRequested` non terminales.
 *   - **Demandes** : demandes avec statuts `en attente` / `acceptée` /
 *     `refusée`. Boutons accept/refuse sur les demandes pending.
 *   - **Journal** : journal partagé (entries newest-first), suppression
 *     possible.
 *
 * Le proto a 2 vues séparées (`/cp/liaison` + `/cp/journal`) ; le shell
 * Nestor n'expose que 5 onglets co-parentage (cf. CLAUDE.md §2.1) — on
 * a fusionné journal en 3e tab. Le CTA « Voir le journal » de CpHome
 * navigue vers `/cp/liaison` (la tab Journal sera sélectionnable
 * manuellement après ; ouverture par défaut sur Transmissions).
 *
 * Garde-fous :
 *   - Pas d'auth en Phase 1 → on autorise les 2 boutons Sign A et
 *     Sign B (le proto les conditionnait à `currentUser.name`).
 *   - Tous les libellés via `t(key)`.
 *   - Suppression journal sans `window.confirm` — la croix discrète.
 */
import { html, type TemplateResult } from 'lit-html';
import { Icon, Icons } from '../../ui/icons.js';
import styles from './LiaisonView.module.css';
import { getLang, t } from '../../i18n/index.js';
import { formatDateTime } from '../../lib/date.js';
import {
  addJournalEntry,
  addRequest,
  addTransmission,
  getCurrentHome,
  removeJournalEntry,
  setRequestStatus,
  signTransmission,
} from '../../stores/homes.js';
import { bumpUi, closeModal, getModal, openModal } from '../../stores/ui.js';
import { modalTemplate } from '../../ui/Modal.js';
import { editorialHero } from '../../ui/Hero.js';
import { interpolate } from '../ai-engine/logic.js';
import type {
  CoparentingHomeData,
  DemandeStatus,
  JournalEntry,
  LiaisonStatus,
  Transmission,
} from '@nestor/shared-types';

const MODAL_TRANSMISSION = 'cp-liaison:transmission';
const MODAL_DEMANDE = 'cp-liaison:demande';
const MODAL_JOURNAL = 'cp-liaison:journal';

type TabKey = 'transmissions' | 'demandes' | 'journal';

interface FormState {
  text: string;
  signatureRequested: boolean;
}

const formState: FormState = { text: '', signatureRequested: false };

let activeTab: TabKey = 'transmissions';

/**
 * Change le tab courant et déclenche un re-render via `bumpUi()` (tab
 * vit en module-local, pas dans un store dédié — pattern documenté
 * dans `stores/ui.ts`).
 */
function selectTab(tab: TabKey): void {
  if (activeTab === tab) return;
  activeTab = tab;
  bumpUi();
}

/** « sam. 11 juil. · 17:00 » — horodatage localisé (audit C, lib/date). */
function formatAt(at: number): string {
  return formatDateTime(at, getLang());
}

// Les valeurs `'brouillon' | 'envoyé' | 'signé A' | …` sont les
// discriminants stables du type `LiaisonStatus` / `DemandeStatus`
// (cf. shared-types). Ce ne sont PAS des libellés UI — `t(key)`
// renvoie le label localisé. D'où les marqueurs `/* i18n-allow */`.
function statusLabel(status: LiaisonStatus | DemandeStatus): string {
  switch (status) {
    case 'brouillon' /* i18n-allow */:
      return t('cp_lia_status_brouillon');
    case 'envoyé' /* i18n-allow */:
      return t('cp_lia_status_envoye');
    case 'signé A' /* i18n-allow */:
      return t('cp_lia_status_signed_a');
    case 'signé B' /* i18n-allow */:
      return t('cp_lia_status_signed_b');
    case 'signé par les deux' /* i18n-allow */:
      return t('cp_lia_status_signed_both');
    case 'en attente' /* i18n-allow */:
      return t('cp_lia_status_pending');
    case 'acceptée' /* i18n-allow */:
      return t('cp_lia_status_accepted');
    case 'refusée' /* i18n-allow */:
      return t('cp_lia_status_refused');
  }
}

function tabsTemplate(): TemplateResult {
  const tabs: { key: TabKey; label: string }[] = [
    { key: 'transmissions', label: t('cp_lia_tab_transmissions') },
    { key: 'demandes', label: t('cp_lia_tab_demandes') },
    { key: 'journal', label: t('cp_lia_tab_journal') },
  ];
  return html`<div class=${styles.tabs} role="tablist" data-testid="cp-lia-tabs">
    ${tabs.map(
      (tab) =>
        html`<button
          type="button"
          role="tab"
          aria-selected=${activeTab === tab.key ? 'true' : 'false'}
          class=${styles.tab}
          data-testid="cp-lia-tab-${tab.key}"
          @click=${(): void => selectTab(tab.key)}
        >
          ${tab.label}
        </button>`,
    )}
  </div>`;
}

// ── Transmissions ──────────────────────────────────────────────────

function transmissionTemplate(homeId: string, entry: Transmission): TemplateResult {
  const status = entry.status as LiaisonStatus;
  // Discriminants `LiaisonStatus` (port shared-types) — pas du libellé UI.
  const isFullySigned = status === 'signé par les deux'; /* i18n-allow */
  const cardClass = isFullySigned ? `${styles.entry} ${styles.entrySignedBoth}` : styles.entry;
  const onSignA = (): void => {
    signTransmission(homeId, entry.id, 'A');
  };
  const onSignB = (): void => {
    signTransmission(homeId, entry.id, 'B');
  };
  const showSignA =
    entry.signatureRequested &&
    status !== 'signé A' /* i18n-allow */ &&
    status !== 'signé par les deux'; /* i18n-allow */
  const showSignB =
    entry.signatureRequested &&
    status !== 'signé B' /* i18n-allow */ &&
    status !== 'signé par les deux'; /* i18n-allow */
  return html`<div class=${cardClass} data-testid="cp-lia-transmission-${entry.id}">
    <div class=${styles.entryHeader}>
      <div>
        <span class=${styles.entryAuthor}
          >${Icon(entry.kind === 'transmission' ? Icons.book : Icons.mail, 14)}</span
        >
        <span class=${styles.entryDate}>${formatAt(entry.at)}</span>
      </div>
      <span class=${styles.entryStatus} data-testid="cp-lia-status-${entry.id}">
        ${statusLabel(entry.status)}
      </span>
    </div>
    <div class=${styles.entryText}>${entry.text}</div>
    ${showSignA || showSignB
      ? html`<div class=${styles.entryActions}>
          ${showSignA
            ? html`<button
                type="button"
                class=${styles.actionBtn}
                data-testid="cp-lia-sign-a-${entry.id}"
                @click=${onSignA}
              >
                ${interpolate(t('cp_lia_sign_a'), { parent: 'A' })}
              </button>`
            : html``}
          ${showSignB
            ? html`<button
                type="button"
                class=${styles.actionBtn}
                data-testid="cp-lia-sign-b-${entry.id}"
                @click=${onSignB}
              >
                ${interpolate(t('cp_lia_sign_b'), { parent: 'B' })}
              </button>`
            : html``}
        </div>`
      : html``}
  </div>`;
}

function transmissionsTabTemplate(
  homeId: string,
  liaison: readonly Transmission[],
): TemplateResult {
  const transmissions = liaison
    .filter((e) => e.kind === 'transmission')
    .slice()
    .sort((a, b) => b.at - a.at);
  const onAdd = (): void => {
    formState.text = '';
    formState.signatureRequested = false;
    openModal(MODAL_TRANSMISSION);
  };
  return html`<div data-testid="cp-lia-transmissions">
    <div class=${styles.addRow}>
      <button
        type="button"
        class=${styles.addPrimary}
        data-testid="cp-lia-btn-new-transmission"
        @click=${onAdd}
      >
        ${t('cp_lia_btn_new_transmission')}
      </button>
    </div>
    ${transmissions.length === 0
      ? html`<div class=${styles.empty} data-testid="cp-lia-transmissions-empty">
          ${t('cp_lia_empty_transmissions')}
        </div>`
      : transmissions.map((e) => transmissionTemplate(homeId, e))}
  </div>`;
}

// ── Demandes ───────────────────────────────────────────────────────

function demandeTemplate(homeId: string, entry: Transmission): TemplateResult {
  const status = entry.status as DemandeStatus;
  // Discriminants `DemandeStatus` (port shared-types) — pas du libellé UI.
  const statusClass =
    status === 'en attente' /* i18n-allow */
      ? `${styles.entryStatus} ${styles.entryStatusPending}`
      : status === 'acceptée' /* i18n-allow */
        ? `${styles.entryStatus} ${styles.entryStatusAccepted}`
        : `${styles.entryStatus} ${styles.entryStatusRefused}`;
  const onAccept = (): void => {
    setRequestStatus(homeId, entry.id, 'acceptée'); /* i18n-allow */
  };
  const onRefuse = (): void => {
    setRequestStatus(homeId, entry.id, 'refusée'); /* i18n-allow */
  };
  return html`<div class=${styles.entry} data-testid="cp-lia-demande-${entry.id}">
    <div class=${styles.entryHeader}>
      <div>
        <span class=${styles.entryAuthor}>${Icon(Icons.mail, 14)}</span>
        <span class=${styles.entryDate}>${formatAt(entry.at)}</span>
      </div>
      <span class=${statusClass} data-testid="cp-lia-status-${entry.id}">
        ${statusLabel(entry.status)}
      </span>
    </div>
    <div class=${styles.entryText}>${entry.text}</div>
    ${status === 'en attente' /* i18n-allow */
      ? html`<div class=${styles.entryActions}>
          <button
            type="button"
            class=${`${styles.actionBtn} ${styles.actionAccept}`}
            data-testid="cp-lia-accept-${entry.id}"
            @click=${onAccept}
          >
            ${t('cp_lia_accept')}
          </button>
          <button
            type="button"
            class=${`${styles.actionBtn} ${styles.actionRefuse}`}
            data-testid="cp-lia-refuse-${entry.id}"
            @click=${onRefuse}
          >
            ${t('cp_lia_refuse')}
          </button>
        </div>`
      : html``}
  </div>`;
}

function demandesTabTemplate(homeId: string, liaison: readonly Transmission[]): TemplateResult {
  const demandes = liaison
    .filter((e) => e.kind === 'demande')
    .slice()
    .sort((a, b) => b.at - a.at);
  const onAdd = (): void => {
    formState.text = '';
    openModal(MODAL_DEMANDE);
  };
  return html`<div data-testid="cp-lia-demandes">
    <div class=${styles.addRow}>
      <button
        type="button"
        class=${styles.addSecondary}
        data-testid="cp-lia-btn-new-demande"
        @click=${onAdd}
      >
        ${t('cp_lia_btn_new_demande')}
      </button>
    </div>
    ${demandes.length === 0
      ? html`<div class=${styles.empty} data-testid="cp-lia-demandes-empty">
          ${t('cp_lia_empty_demandes')}
        </div>`
      : demandes.map((e) => demandeTemplate(homeId, e))}
  </div>`;
}

// ── Journal ────────────────────────────────────────────────────────

function journalEntryTemplate(homeId: string, entry: JournalEntry): TemplateResult {
  const onRemove = (): void => {
    removeJournalEntry(homeId, entry.id);
  };
  return html`<div class=${styles.entry} data-testid="cp-lia-journal-${entry.id}">
    <div class=${styles.entryHeader}>
      <div>
        <span class=${styles.entryAuthor}>${Icon(Icons.book, 14)} ${entry.who ?? ''}</span>
        <span class=${styles.entryDate}>${formatAt(entry.at)}</span>
      </div>
      <button
        type="button"
        class=${styles.removeBtn}
        data-testid="cp-lia-journal-remove-${entry.id}"
        aria-label="Remove"
        @click=${onRemove}
      >
        ×
      </button>
    </div>
    <div class=${styles.entryText}>${entry.text}</div>
  </div>`;
}

function journalTabTemplate(homeId: string, journal: readonly JournalEntry[]): TemplateResult {
  const sorted = journal.slice().sort((a, b) => b.at - a.at);
  const onAdd = (): void => {
    formState.text = '';
    openModal(MODAL_JOURNAL);
  };
  return html`<div data-testid="cp-lia-journal">
    <div class=${styles.intro} data-testid="cp-lia-journal-intro">${t('cp_lia_journal_intro')}</div>
    <div class=${styles.addRow} style="margin-top: 0.6rem">
      <button
        type="button"
        class=${styles.addPrimary}
        data-testid="cp-lia-btn-new-journal"
        @click=${onAdd}
      >
        ${t('cp_lia_btn_new_journal')}
      </button>
    </div>
    ${sorted.length === 0
      ? html`<div class=${styles.empty} data-testid="cp-lia-journal-empty">
          ${t('cp_lia_empty_journal')}
        </div>`
      : sorted.map((e) => journalEntryTemplate(homeId, e))}
  </div>`;
}

// ── Modals ─────────────────────────────────────────────────────────

function transmissionFormTemplate(homeId: string): TemplateResult {
  const onSubmit = (e: Event): void => {
    e.preventDefault();
    if (!formState.text.trim()) return;
    addTransmission(homeId, {
      text: formState.text,
      signatureRequested: formState.signatureRequested,
    });
    formState.text = '';
    formState.signatureRequested = false;
    closeModal();
  };
  return html`<form @submit=${onSubmit}>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_lia_label_text')}</label>
      <textarea
        class=${styles.formTextarea}
        data-testid="cp-lia-form-text"
        placeholder=${t('cp_lia_placeholder_transmission')}
        .value=${formState.text}
        @input=${(e: Event): void => {
          formState.text = (e.target as HTMLTextAreaElement).value;
        }}
        required
      ></textarea>
    </div>
    <label class=${styles.formCheckRow}>
      <input
        type="checkbox"
        data-testid="cp-lia-form-signature"
        .checked=${formState.signatureRequested}
        @change=${(e: Event): void => {
          formState.signatureRequested = (e.target as HTMLInputElement).checked;
        }}
      />
      ${t('cp_lia_signature_requested')}
    </label>
    <button type="submit" class=${styles.formSubmit} data-testid="cp-lia-form-submit">
      ${t('cp_lia_btn_send')}
    </button>
  </form>`;
}

function demandeFormTemplate(homeId: string): TemplateResult {
  const onSubmit = (e: Event): void => {
    e.preventDefault();
    if (!formState.text.trim()) return;
    addRequest(homeId, formState.text);
    formState.text = '';
    closeModal();
  };
  return html`<form @submit=${onSubmit}>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_lia_label_text')}</label>
      <textarea
        class=${styles.formTextarea}
        data-testid="cp-lia-form-text"
        placeholder=${t('cp_lia_placeholder_demande')}
        .value=${formState.text}
        @input=${(e: Event): void => {
          formState.text = (e.target as HTMLTextAreaElement).value;
        }}
        required
      ></textarea>
    </div>
    <button type="submit" class=${styles.formSubmit} data-testid="cp-lia-form-submit">
      ${t('cp_lia_btn_send')}
    </button>
  </form>`;
}

function journalFormTemplate(homeId: string): TemplateResult {
  const onSubmit = (e: Event): void => {
    e.preventDefault();
    if (!formState.text.trim()) return;
    addJournalEntry(homeId, { text: formState.text });
    formState.text = '';
    closeModal();
  };
  return html`<form @submit=${onSubmit}>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_lia_label_text')}</label>
      <textarea
        class=${styles.formTextarea}
        data-testid="cp-lia-form-text"
        placeholder=${t('cp_lia_placeholder_journal')}
        .value=${formState.text}
        @input=${(e: Event): void => {
          formState.text = (e.target as HTMLTextAreaElement).value;
        }}
        required
      ></textarea>
    </div>
    <button type="submit" class=${styles.formSubmit} data-testid="cp-lia-form-submit">
      ${t('cp_lia_btn_add')}
    </button>
  </form>`;
}

// ── Public render ──────────────────────────────────────────────────

export function renderCpLiaisonView(): TemplateResult {
  const home = getCurrentHome();
  if (!home || home.mode !== 'coparenting') {
    return html`<section class=${styles.view} data-testid="view-cp-liaison">
      <p class=${styles.empty}>${t('cp_home_no_custody')}</p>
    </section>`;
  }
  const data = home.data as CoparentingHomeData;
  const modal = getModal();

  return html`
    <section class=${styles.view} data-testid="view-cp-liaison">
      ${editorialHero({
        badge: { mode: 'coparenting', label: home.name },
        title: t('cp_lia_hero_title'),
        accent: t('cp_lia_hero_accent'),
      })}
      <div class=${styles.intro} data-testid="cp-lia-intro">${t('cp_lia_intro')}</div>
      ${tabsTemplate()}
      ${activeTab === 'transmissions'
        ? transmissionsTabTemplate(home.id, data.liaison)
        : activeTab === 'demandes'
          ? demandesTabTemplate(home.id, data.liaison)
          : journalTabTemplate(home.id, data.journal)}
      ${modal === MODAL_TRANSMISSION
        ? modalTemplate({
            title: t('cp_lia_modal_transmission_title'),
            content: transmissionFormTemplate(home.id),
            onClose: closeModal,
            testid: 'cp-lia-modal-transmission',
          })
        : html``}
      ${modal === MODAL_DEMANDE
        ? modalTemplate({
            title: t('cp_lia_modal_demande_title'),
            content: demandeFormTemplate(home.id),
            onClose: closeModal,
            testid: 'cp-lia-modal-demande',
          })
        : html``}
      ${modal === MODAL_JOURNAL
        ? modalTemplate({
            title: t('cp_lia_modal_journal_title'),
            content: journalFormTemplate(home.id),
            onClose: closeModal,
            testid: 'cp-lia-modal-journal',
          })
        : html``}
    </section>
  `;
}

/** @internal pour tests : reset l'onglet + form. */
export function _resetCpLiaisonForTests(): void {
  activeTab = 'transmissions';
  formState.text = '';
  formState.signatureRequested = false;
}
